import { invitationDeliveryResponseSchema, wave2ApiRoutes } from "@lifescapsule/contracts";
import { apiRequest } from "../../shared/api";

export type CanonicalFamilyCollection = "MEMBERS" | "INVITATIONS" | "ACTIVITY";
export type CanonicalFamilyRole = "OWNER" | "EDITOR" | "CONTRIBUTOR" | "VIEWER";

export type CanonicalFamilyViewer = {
  memberId: string;
  role: CanonicalFamilyRole;
};

export type CanonicalFamilyMemberSummary = {
  memberId: string;
  role: CanonicalFamilyRole;
  responsibility: "STORYTELLER" | "VIEWER_RECIPIENT" | "NONE";
  contributionStatus: "NOT_STARTED" | "IN_PROGRESS" | "SUBMITTED" | "APPROVED";
  joinedAt: string;
  /** A hint of their sign-in email, shown only to those who manage the book (E11). */
  emailHint?: string;
};

export type CanonicalFamilyInvitationSummary = {
  invitationId: string;
  role: "EDITOR" | "CONTRIBUTOR" | "VIEWER";
  status: "PENDING" | "ACCEPTED" | "DECLINED" | "EXPIRED" | "REVOKED";
  createdAt: string;
  expiresAt: number;
  /** Whether its email went out; absent on invitations made before it was recorded. */
  deliveryState?: "PENDING" | "SENT" | "NEEDS_ATTENTION";
  /** When it was last sent again, if it has been. */
  lastSentAt?: string;
  /** A hint of the invited address, shown only to those who manage the book (E11). */
  emailHint?: string;
};

export type CanonicalFamilyActivitySummary = {
  eventId: string;
  actorMemberId: string;
  targetEntityId: string;
  eventType:
    | "CONSENT_GRANTED"
    | "CONSENT_REVOKED"
    | "MEMBER_REMOVED"
    | "MEMBER_ROLE_CHANGED"
    | "CONTRIBUTION_STATUS_CHANGED"
    | "INVITATION_REVOKED"
    | "PROMPT_ASSIGNED"
    | "CHAPTER_ASSIGNED";
  occurredAt: string;
  statusCode: "RECORDED" | "REVOKED" | "REMOVED" | "ASSIGNED" | "CHANGED";
};

type CollectionItem = {
  MEMBERS: CanonicalFamilyMemberSummary;
  INVITATIONS: CanonicalFamilyInvitationSummary;
  ACTIVITY: CanonicalFamilyActivitySummary;
};

export type CanonicalFamilyPage<T> = {
  items: T[];
  page: { hasMore: boolean; nextCursor: string | null };
};

type CanonicalFamilyCollectionPage<T> = CanonicalFamilyPage<T> & {
  viewer: CanonicalFamilyViewer;
};

export type CanonicalFamilySnapshot = {
  viewer: CanonicalFamilyViewer;
  members: CanonicalFamilyPage<CanonicalFamilyMemberSummary>;
  invitations: CanonicalFamilyPage<CanonicalFamilyInvitationSummary>;
  activity: CanonicalFamilyPage<CanonicalFamilyActivitySummary>;
};

export type CanonicalFamilyMutationResult<S extends string> = {
  entityId: string;
  statusCode: S;
  version: number;
  replayed: boolean;
  notification: { mode: "SANDBOX"; status: "UNAVAILABLE" };
};

export type CanonicalInvitationRevokeResult = CanonicalFamilyMutationResult<"INVITATION_REVOKED">;

export type CanonicalInvitationCreateResult = {
  invitationId: string;
  deliveryState: "PENDING" | "SENT" | "NEEDS_ATTENTION";
  /** True for a test account's invitation; a real customer's is real (D17). */
  test: boolean;
};

const ulid = /^[0-9A-HJKMNP-TV-Z]{26}$/iu;

function record(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  if (
    typeof value !== "object" ||
    value === null ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !keys.includes(key))
  )
    throw new Error(`The Family Collaboration API returned an invalid ${label}.`);
  return value as Record<string, unknown>;
}

function requiredString(value: unknown, allowed?: readonly string[]) {
  if (typeof value !== "string" || (allowed && !allowed.includes(value)))
    throw new Error("The Family Collaboration API returned an invalid string field.");
  return value;
}

function requiredId(value: unknown) {
  const id = requiredString(value);
  if (!ulid.test(id)) throw new Error("The Family Collaboration API returned an invalid ID.");
  return id;
}

function requiredTimestamp(value: unknown) {
  const timestamp = requiredString(value);
  const parsed = Date.parse(timestamp);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== timestamp)
    throw new Error("The Family Collaboration API returned an invalid timestamp.");
  return timestamp;
}

function optionalHint(value: unknown) {
  if (value === undefined) return {};
  if (typeof value !== "string" || value.length < 3 || value.length > 260 || !value.includes("@"))
    throw new Error("The Family Collaboration API returned an invalid email hint.");
  return { emailHint: value };
}

function member(value: unknown): CanonicalFamilyMemberSummary {
  const item = record(
    value,
    ["memberId", "role", "responsibility", "contributionStatus", "joinedAt", "emailHint"],
    "member",
  );
  return {
    ...optionalHint(item.emailHint),
    memberId: requiredId(item.memberId),
    role: requiredString(item.role, ["OWNER", "EDITOR", "CONTRIBUTOR", "VIEWER"]),
    responsibility: requiredString(item.responsibility, [
      "STORYTELLER",
      "VIEWER_RECIPIENT",
      "NONE",
    ]),
    contributionStatus: requiredString(item.contributionStatus, [
      "NOT_STARTED",
      "IN_PROGRESS",
      "SUBMITTED",
      "APPROVED",
    ]),
    joinedAt: requiredTimestamp(item.joinedAt),
  } as CanonicalFamilyMemberSummary;
}

function invitation(value: unknown): CanonicalFamilyInvitationSummary {
  const item = record(
    value,
    [
      "invitationId",
      "role",
      "status",
      "createdAt",
      "expiresAt",
      "deliveryState",
      "lastSentAt",
      "emailHint",
    ],
    "invitation",
  );
  if (!Number.isSafeInteger(item.expiresAt) || Number(item.expiresAt) < 0)
    throw new Error("The Family Collaboration API returned an invalid invitation expiry.");
  return {
    ...optionalHint(item.emailHint),
    ...(item.deliveryState === undefined
      ? {}
      : {
          deliveryState: requiredString(item.deliveryState, ["PENDING", "SENT", "NEEDS_ATTENTION"]),
        }),
    ...(item.lastSentAt === undefined ? {} : { lastSentAt: requiredTimestamp(item.lastSentAt) }),
    invitationId: requiredId(item.invitationId),
    role: requiredString(item.role, ["EDITOR", "CONTRIBUTOR", "VIEWER"]),
    status: requiredString(item.status, ["PENDING", "ACCEPTED", "DECLINED", "EXPIRED", "REVOKED"]),
    createdAt: requiredTimestamp(item.createdAt),
    expiresAt: Number(item.expiresAt),
  } as CanonicalFamilyInvitationSummary;
}

function activity(value: unknown): CanonicalFamilyActivitySummary {
  const item = record(
    value,
    ["eventId", "actorMemberId", "targetEntityId", "eventType", "occurredAt", "statusCode"],
    "activity",
  );
  return {
    eventId: requiredId(item.eventId),
    actorMemberId: requiredId(item.actorMemberId),
    targetEntityId: requiredId(item.targetEntityId),
    eventType: requiredString(item.eventType, [
      "CONSENT_GRANTED",
      "CONSENT_REVOKED",
      "MEMBER_REMOVED",
      "MEMBER_ROLE_CHANGED",
      "CONTRIBUTION_STATUS_CHANGED",
      "INVITATION_REVOKED",
      "PROMPT_ASSIGNED",
      "CHAPTER_ASSIGNED",
    ]),
    occurredAt: requiredTimestamp(item.occurredAt),
    statusCode: requiredString(item.statusCode, [
      "RECORDED",
      "REVOKED",
      "REMOVED",
      "ASSIGNED",
      "CHANGED",
    ]),
  } as CanonicalFamilyActivitySummary;
}

function viewer(value: unknown): CanonicalFamilyViewer {
  const item = record(value, ["memberId", "role"], "viewer");
  return {
    memberId: requiredId(item.memberId),
    role: requiredString(item.role, ["OWNER", "EDITOR", "CONTRIBUTOR", "VIEWER"]),
  } as CanonicalFamilyViewer;
}

const parsers = { MEMBERS: member, INVITATIONS: invitation, ACTIVITY: activity } as const;

function parsePage<C extends CanonicalFamilyCollection>(
  payload: unknown,
  collection: C,
): CanonicalFamilyCollectionPage<CollectionItem[C]> {
  const envelope = record(payload, ["data", "capability"], "response");
  if (envelope.capability !== "WORKING")
    throw new Error("The Family Collaboration API capability was not Working.");
  const data = record(envelope.data, ["viewer", "items", "page"], "page");
  if (!Array.isArray(data.items) || data.items.length > 100)
    throw new Error("The Family Collaboration API returned an invalid item page.");
  const page = record(data.page, ["hasMore", "nextCursor"], "page boundary");
  if (
    typeof page.hasMore !== "boolean" ||
    !(
      page.nextCursor === null ||
      (typeof page.nextCursor === "string" &&
        page.nextCursor.length > 0 &&
        page.nextCursor.length <= 2_048)
    ) ||
    page.hasMore !== (page.nextCursor !== null)
  )
    throw new Error("The Family Collaboration API returned an invalid page boundary.");
  const parseItem: (item: unknown) => CollectionItem[C] = parsers[collection] as (
    item: unknown,
  ) => CollectionItem[C];
  return {
    viewer: viewer(data.viewer),
    items: data.items.map(parseItem),
    page: { hasMore: page.hasMore, nextCursor: page.nextCursor as string | null },
  };
}

export async function listCanonicalFamilyCollection<C extends CanonicalFamilyCollection>(
  bookId: string,
  idToken: string,
  collection: C,
): Promise<CanonicalFamilyCollectionPage<CollectionItem[C]>> {
  // The delivery state and email hints come only to a page that asks (P2-8, 2026-11-02).
  const query = new URLSearchParams({ collection, limit: "50", responseVersion: "2" });
  const payload = await apiRequest<unknown>(
    `${wave2ApiRoutes.collaboration(bookId)}?${query.toString()}`,
    idToken,
  );
  return parsePage(payload, collection);
}

export async function loadCanonicalFamilySnapshot(
  bookId: string,
  idToken: string,
): Promise<CanonicalFamilySnapshot> {
  const [members, invitations, activityPage] = await Promise.all([
    listCanonicalFamilyCollection(bookId, idToken, "MEMBERS"),
    listCanonicalFamilyCollection(bookId, idToken, "INVITATIONS"),
    listCanonicalFamilyCollection(bookId, idToken, "ACTIVITY"),
  ]);
  const viewerKey = `${members.viewer.memberId}:${members.viewer.role}`;
  if (
    `${invitations.viewer.memberId}:${invitations.viewer.role}` !== viewerKey ||
    `${activityPage.viewer.memberId}:${activityPage.viewer.role}` !== viewerKey
  )
    throw new Error("The Family Collaboration API returned inconsistent viewer authority.");
  return {
    viewer: members.viewer,
    members: { items: members.items, page: members.page },
    invitations: { items: invitations.items, page: invitations.page },
    activity: { items: activityPage.items, page: activityPage.page },
  };
}

function parseFamilyMutation<S extends string>(
  payload: unknown,
  entityId: string,
  statusCode: S,
): CanonicalFamilyMutationResult<S> {
  const envelope = record(payload, ["data", "capability"], "mutation response");
  if (envelope.capability !== "WORKING")
    throw new Error("The Family Collaboration API capability was not Working.");
  const data = record(
    envelope.data,
    ["result", "version", "replayed", "notification"],
    "mutation result",
  );
  const result = record(data.result, ["entityId", "statusCode"], "mutation entity");
  const notification = record(data.notification, ["mode", "status"], "notification boundary");
  if (
    requiredId(result.entityId) !== entityId ||
    result.statusCode !== statusCode ||
    !Number.isSafeInteger(data.version) ||
    Number(data.version) < 1 ||
    typeof data.replayed !== "boolean" ||
    notification.mode !== "SANDBOX" ||
    notification.status !== "UNAVAILABLE"
  )
    throw new Error("The Family Collaboration API returned invalid family-change evidence.");
  return {
    entityId,
    statusCode,
    version: Number(data.version),
    replayed: data.replayed,
    notification: { mode: "SANDBOX", status: "UNAVAILABLE" },
  };
}

export async function revokeCanonicalFamilyInvitation(
  bookId: string,
  idToken: string,
  invitationId: string,
  idempotencyKey: string,
): Promise<CanonicalInvitationRevokeResult> {
  if (idempotencyKey.length < 16 || idempotencyKey.length > 128)
    throw new Error("A stable family command idempotency key is required.");
  const payload = await apiRequest<unknown>(wave2ApiRoutes.collaboration(bookId), idToken, {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey },
    body: JSON.stringify({ type: "REVOKE_INVITATION", invitationId }),
  });
  return parseFamilyMutation(payload, invitationId, "INVITATION_REVOKED");
}

/** Takes someone out of the book: they can no longer open it. */
export async function removeCanonicalFamilyMember(
  bookId: string,
  idToken: string,
  memberId: string,
  idempotencyKey: string,
): Promise<CanonicalFamilyMutationResult<"MEMBER_REMOVED">> {
  if (idempotencyKey.length < 16 || idempotencyKey.length > 128)
    throw new Error("A stable family command idempotency key is required.");
  const payload = await apiRequest<unknown>(wave2ApiRoutes.collaboration(bookId), idToken, {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey },
    body: JSON.stringify({
      type: "REMOVE_MEMBER",
      targetMemberId: memberId,
      reasonCode: "ACCESS_REVOKED",
    }),
  });
  return parseFamilyMutation(payload, memberId, "MEMBER_REMOVED");
}

/** Changes what someone may do in the book, keeping the rest of their place in it. */
export async function changeCanonicalFamilyMemberRole(
  bookId: string,
  idToken: string,
  input: {
    memberId: string;
    role: "EDITOR" | "CONTRIBUTOR" | "VIEWER";
    responsibility: CanonicalFamilyMemberSummary["responsibility"];
  },
  idempotencyKey: string,
): Promise<CanonicalFamilyMutationResult<"MEMBER_ROLE_CHANGED">> {
  if (idempotencyKey.length < 16 || idempotencyKey.length > 128)
    throw new Error("A stable family command idempotency key is required.");
  const payload = await apiRequest<unknown>(wave2ApiRoutes.collaboration(bookId), idToken, {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey },
    body: JSON.stringify({
      type: "CHANGE_MEMBER_ROLE",
      targetMemberId: input.memberId,
      role: input.role,
      responsibility: input.responsibility,
    }),
  });
  return parseFamilyMutation(payload, input.memberId, "MEMBER_ROLE_CHANGED");
}

/**
 * Sends a pending invitation again (E11). The email carries a new link, and the earlier link
 * stops working. A retry with the same key never sends a second email.
 */
export async function resendCanonicalFamilyInvitation(
  bookId: string,
  idToken: string,
  invitationId: string,
  idempotencyKey: string,
): Promise<CanonicalInvitationCreateResult> {
  if (idempotencyKey.length < 16 || idempotencyKey.length > 128)
    throw new Error("A stable invitation idempotency key is required.");
  // Resending is a command on the invitation route itself (no route of its own).
  const payload = await apiRequest<unknown>(wave2ApiRoutes.invitations(bookId), idToken, {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey },
    body: JSON.stringify({ action: "RESEND", invitationId }),
  });
  const { data } = invitationDeliveryResponseSchema.parse(payload);
  if (data.invitationId !== invitationId)
    throw new Error("The invitation API answered for another invitation.");
  return data;
}

export async function createCanonicalFamilyInvitation(
  bookId: string,
  idToken: string,
  input: { email: string; role: "EDITOR" | "CONTRIBUTOR" | "VIEWER" },
  idempotencyKey: string,
): Promise<CanonicalInvitationCreateResult> {
  if (idempotencyKey.length < 16 || idempotencyKey.length > 128)
    throw new Error("A stable invitation idempotency key is required.");
  const payload = await apiRequest<unknown>(wave2ApiRoutes.invitations(bookId), idToken, {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey },
    body: JSON.stringify(input),
  });
  // A real customer's invitation answers test: false (D17); both are valid answers.
  return invitationDeliveryResponseSchema.parse(payload).data;
}
